import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  AiCompanionSessionContext,
  getRecapProfile,
  getRecapValidationErrors,
  SaveOnboardingDetailsFields,
  TypeOfUser,
} from '@pharmaplan/common';

import CustomButton from '../../common/CustomButton';
import CustomControlledTextField from '../../common/CustomControlledTextField';
import strings from '../../../localization';

import { OnboardingSnapshot } from './types';
import AiSoftwareExperienceTable from './AiSoftwareExperienceTable';
import styles from './styles';

interface IOnboardingRecapDialog {
  open: boolean;
  sessionContext: AiCompanionSessionContext;
  snapshot: OnboardingSnapshot;
  onConfirm: (profile: SaveOnboardingDetailsFields) => Promise<string[]>;
}

type RecapFieldName = keyof SaveOnboardingDetailsFields;
type RecapTextFieldName =
  | 'licenseNumber'
  | 'graduationYear'
  | 'diploma'
  | 'instituteName'
  | 'techGraduationYear'
  | 'otherLanguages'
  | 'address'
  | 'city'
  | 'postalCode'
  | 'phone'
  | 'mobile'
  | 'yearsOfExperience'
  | 'preferredDistanceRange';

const languageLevels = [
  { label: strings.weakLevel, value: 0 },
  { label: strings.goodLevel, value: 1 },
  { label: strings.fluentLevel, value: 2 },
];

// Working days use Date#getDay numbering (Sunday = 0 … Saturday = 6), the same
// values manual signup saves, so a Sunday-first list maps directly to its index.
const workingDays = [
  strings.sunday,
  strings.monday,
  strings.tuesday,
  strings.wednesday,
  strings.thursday,
  strings.friday,
  strings.saturday,
].map((label, index) =>
  ({
    label,
    value: index,
  }));

const getNullableNumber = (value: string) =>
  (value.trim() ? Number(value) : null);

const OnboardingRecapDialog = ({
  open,
  sessionContext,
  snapshot,
  onConfirm,
}: IOnboardingRecapDialog) => {
  const [profile, setProfile] = useState<SaveOnboardingDetailsFields | null>(
    null,
  );
  const [validationErrors, setValidationErrors] = useState<
  Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setProfile(getRecapProfile(snapshot.onboardingDetails));
      setValidationErrors({});
      setIsSubmitting(false);
    }
  }, [open, snapshot.onboardingDetails]);

  const updateField = <FieldName extends RecapFieldName>(
    fieldName: FieldName,
    value: SaveOnboardingDetailsFields[FieldName],
  ) => {
    setProfile((currentProfile) =>
      (currentProfile
        ? { ...currentProfile, [fieldName]: value }
        : currentProfile));
    setValidationErrors((currentErrors) => {
      const remainingErrors = { ...currentErrors };
      delete remainingErrors[fieldName];
      return remainingErrors;
    });
  };

  const handleConfirm = async () => {
    if (!profile || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const errors = await onConfirm(profile);
    setValidationErrors(getRecapValidationErrors(errors));

    if (errors.length > 0) {
      setIsSubmitting(false);
    }
  };

  const toggleStringSelection = (
    fieldName: 'selectedContactPreferences',
    value: string,
  ) => {
    if (!profile) {
      return;
    }

    const currentValues = profile[fieldName];
    updateField(
      fieldName,
      currentValues.includes(value)
        ? currentValues.filter((currentValue) =>
          currentValue !== value)
        : [...currentValues, value],
    );
  };

  const toggleWorkingDay = (value: number) => {
    if (!profile) {
      return;
    }

    updateField(
      'preferredWorkingDays',
      profile.preferredWorkingDays.includes(value)
        ? profile.preferredWorkingDays.filter((day) =>
          day !== value)
        : [...profile.preferredWorkingDays, value],
    );
  };

  const fieldError = (fieldName: RecapFieldName) =>
    validationErrors[fieldName];

  const renderTextField = (
    fieldName: RecapTextFieldName,
    label: string,
    type: 'text' | 'number' = 'text',
  ) => {
    if (!profile) {
      return null;
    }

    const value = profile[fieldName];

    return (
      <CustomControlledTextField
        error={Boolean(fieldError(fieldName))}
        fullWidth
        helperText={fieldError(fieldName)}
        label={label}
        name={fieldName}
        type={type}
        value={value ?? ''}
        onChange={(event) => {
          const nextValue = type === 'number'
            ? getNullableNumber(event.target.value)
            : event.target.value;
          updateField(fieldName, nextValue as never);
        }}
      />
    );
  };

  const role = profile?.role;
  const isAvailabilityReminderEnabled = profile?.availabilityReminder ?? true;

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle sx={styles.recapTitle}>
        {strings.reviewYourInformation}
      </DialogTitle>
      <DialogContent dividers sx={styles.recapContent}>
        <Typography sx={styles.recapDescription}>
          {strings.reviewInformationDescription}
        </Typography>

        {validationErrors.input ? (
          <Typography color="error" role="alert" sx={styles.recapError}>
            {validationErrors.input}
          </Typography>
        ) : null}

        {profile ? (
          <Box sx={styles.recapGrid}>
            <Typography
              sx={[styles.recapFullWidthField, styles.recapGroupLabel]}
            >
              {strings.professionalInformation}
            </Typography>
            <CustomControlledTextField
              disabled
              fullWidth
              label={strings.role}
              name="role"
              value={profile.role}
            />
            <CustomControlledTextField
              disabled
              fullWidth
              label={strings.email}
              name="email"
              value={sessionContext.email}
            />
            <CustomControlledTextField
              disabled
              fullWidth
              label={strings.preferredLanguage}
              name="preferredLanguage"
              value={sessionContext.preferredLanguage}
            />
            <CustomControlledTextField
              disabled
              fullWidth
              label={strings.firstName}
              name="firstName"
              value={sessionContext.firstName}
            />
            <CustomControlledTextField
              disabled
              fullWidth
              label={strings.lastName}
              name="lastName"
              value={sessionContext.lastName}
            />

            <CustomControlledTextField
              error={Boolean(fieldError('professionalCapacityId'))}
              fullWidth
              helperText={fieldError('professionalCapacityId')}
              label={strings.professionalCapacity}
              name="professionalCapacityId"
              select
              value={profile.professionalCapacityId}
              onChange={(event) =>
                updateField('professionalCapacityId', event.target.value)}
            >
              {(sessionContext.professionalCapacityOptions ?? []).map(
                ({ description, professionalCapacityId }) =>
                  (
                    <MenuItem
                      key={professionalCapacityId}
                      value={professionalCapacityId}
                    >
                      {description}
                    </MenuItem>
                  ),
              )}
            </CustomControlledTextField>

            {role === TypeOfUser.pharmacist
              ? renderTextField('licenseNumber', strings.licenseNo)
              : null}
            {role !== TypeOfUser.student
              ? renderTextField(
                'graduationYear',
                role === TypeOfUser.technician
                  ? strings.techExperienceSince
                  : strings.yearLicensed,
                'number',
              )
              : null}
            {renderTextField(
              'yearsOfExperience',
              strings.yearsOfExperience,
              'number',
            )}
            {role === TypeOfUser.technician ? (
              <>
                {renderTextField('diploma', strings.diploma)}
                {renderTextField('instituteName', strings.educationalInstitute)}
                {renderTextField(
                  'techGraduationYear',
                  strings.graduationYear,
                  'number',
                )}
              </>
            ) : null}

            {(['englishLevel', 'frenchLevel'] as const).map((fieldName) =>
              (
                <CustomControlledTextField
                  key={fieldName}
                  error={Boolean(fieldError(fieldName))}
                  fullWidth
                  helperText={fieldError(fieldName)}
                  label={fieldName === 'englishLevel'
                    ? strings.englishLevel
                    : strings.frenchLevel}
                  name={fieldName}
                  select
                  value={profile[fieldName]}
                  onChange={(event) =>
                    updateField(fieldName, Number(event.target.value) as 0 | 1 | 2)}
                >
                  {languageLevels.map(({ label, value }) =>
                    (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                </CustomControlledTextField>
              ))}
            {renderTextField('otherLanguages', strings.otherLanguage)}

            {renderTextField('address', strings.address)}
            {renderTextField('city', strings.city)}
            <CustomControlledTextField
              error={Boolean(fieldError('province'))}
              fullWidth
              helperText={fieldError('province')}
              label={strings.province}
              name="province"
              select
              value={profile.province}
              onChange={(event) =>
                updateField('province', event.target.value)}
            >
              {(sessionContext.provinceOptions ?? []).map(({ key, name }) =>
                (
                  <MenuItem key={key} value={key}>{name}</MenuItem>
                ))}
            </CustomControlledTextField>
            {renderTextField('postalCode', strings.postalCode)}
            {renderTextField('phone', strings.homePhone)}
            {renderTextField('mobile', strings.mobile)}

            <Box sx={styles.recapFullWidthField}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={profile.incorporated}
                    onChange={(event) =>
                      updateField('incorporated', event.target.checked)}
                  />
                )}
                label={strings.areYouIncorporated}
              />
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={profile.isDSQKeyAvailable}
                    onChange={(event) =>
                      updateField('isDSQKeyAvailable', event.target.checked)}
                  />
                )}
                label={strings.doYouHaveOwnDsqKey}
              />
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={profile.isMethadonePrepKnowledge}
                    onChange={(event) =>
                      updateField('isMethadonePrepKnowledge', event.target.checked)}
                  />
                )}
                label={strings.familiarWithMethadonePreparation}
              />
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={profile.isComfortDispillVerification}
                    onChange={(event) =>
                      updateField('isComfortDispillVerification', event.target.checked)}
                  />
                )}
                label={strings.comfortableWithDispillVerification}
              />
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={isAvailabilityReminderEnabled}
                    onChange={(event) =>
                      updateField('availabilityReminder', event.target.checked)}
                  />
                )}
                label={strings.availabilityReminder}
              />
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={profile.remoteRegion}
                    onChange={(event) =>
                      updateField('remoteRegion', event.target.checked)}
                  />
                )}
                label={strings.remoteRegions}
              />
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <Typography sx={styles.recapGroupLabel}>
                {strings.selectedContactPreference}
              </Typography>
              <FormGroup row>
                {(sessionContext.contactPreferenceOptions ?? []).map(
                  ({ contactPreferenceId, name }) =>
                    (
                      <FormControlLabel
                        key={contactPreferenceId}
                        control={(
                          <Checkbox
                            checked={profile.selectedContactPreferences.includes(
                              contactPreferenceId,
                            )}
                            onChange={() =>
                              toggleStringSelection(
                                'selectedContactPreferences',
                                contactPreferenceId,
                              )}
                          />
                      )}
                        label={name}
                      />
                    ),
                )}
              </FormGroup>
              {fieldError('selectedContactPreferences') ? (
                <FormHelperText error>
                  {fieldError('selectedContactPreferences')}
                </FormHelperText>
              ) : null}
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <Typography sx={styles.recapGroupLabel}>{strings.software}</Typography>
              <Box sx={styles.recapSoftwareGrid}>
                <AiSoftwareExperienceTable
                  selectedSoftwares={profile.selectedSoftwares}
                  softwareOptions={sessionContext.softwareOptions ?? []}
                  onChange={(selectedSoftwares) =>
                    updateField('selectedSoftwares', selectedSoftwares)}
                />
              </Box>
            </Box>

            <Box sx={styles.recapFullWidthField}>
              <Typography sx={styles.recapGroupLabel}>
                {strings.preferredWorkingDays}
              </Typography>
              <FormGroup row>
                {workingDays.map(({ label, value }) =>
                  (
                    <FormControlLabel
                      key={value}
                      control={(
                        <Checkbox
                          checked={profile.preferredWorkingDays.includes(value)}
                          onChange={() =>
                            toggleWorkingDay(value)}
                        />
                    )}
                      label={label}
                    />
                  ))}
              </FormGroup>
              {fieldError('preferredWorkingDays') ? (
                <FormHelperText error>
                  {fieldError('preferredWorkingDays')}
                </FormHelperText>
              ) : null}
            </Box>

            <CustomControlledTextField
              error={Boolean(fieldError('preferredReplacementDuration'))}
              fullWidth
              helperText={fieldError('preferredReplacementDuration')}
              label={strings.preferredReplacementDuration}
              name="preferredReplacementDuration"
              select
              value={profile.preferredReplacementDuration}
              onChange={(event) =>
                updateField('preferredReplacementDuration', event.target.value)}
            >
              {(sessionContext.preferredReplacementDurationOptions ?? []).map(
                (duration) =>
                  (
                    <MenuItem key={duration} value={duration}>{duration}</MenuItem>
                  ),
              )}
            </CustomControlledTextField>
            {renderTextField(
              'preferredDistanceRange',
              strings.preferredDistanceRadius,
              'number',
            )}

          </Box>
        ) : (
          <Typography color="error" role="alert">
            {strings.aiCompanionConfigurationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={styles.recapActions}>
        <CustomButton
          disabled={!profile || isSubmitting}
          type="button"
          fullWidth={false}
          label={strings.confirmAndSubmit}
          onClick={handleConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingRecapDialog;
